import { Product } from './Product';

/**
 * Central region engine — the single rule the whole system pivots on.
 *
 * The Electus B2B catalogue is SHARED across the AU and NZ storefronts in
 * commercetools (one product-selection backs both regions; only the supply
 * channel differs). Whether a product is sold in NZ vs AU-only lives in ONE
 * place on the product: the variant `businessUnits` set attribute (the region
 * split is otherwise modelled only in the external Algolia indexer).
 *
 * The pivot vocabulary is the `siteCode` (`sdau`/`sdnz`) — the same token used
 * on products (`businessUnits` keys), the order/customer/channel custom fields,
 * and the business-unit `siteCode` field. `siteCodeFromLocale` is THE rule that
 * maps a storefront request to its site, and it is shared by both the Algolia
 * index selection (frontend) and the product gate (backend) so they can never
 * drift.
 *
 * Pure (depends only on the Product type) so it lives in the shared `types`
 * package that both the frontend and backend import. See the
 * project_nz_region_gate memory.
 */

export type SiteCode = 'sdau' | 'sdnz';

/**
 * The site a storefront request belongs to, derived from its locale.
 * `en-NZ` → `sdnz`; everything else (`en-AU` + the dev default) → `sdau`.
 *
 * This is THE locale→region rule. The frontend uses it to pick the Algolia
 * index (`{env}_products_{siteCode}`) and the backend uses it to gate CT
 * product reads — one function, so the PLP and the PDP/gate stay consistent.
 * When EWM-522 splits the domains, the locale is resolved from the host instead
 * of the path, but this mapping is unchanged.
 */
export const siteCodeFromLocale = (locale: string): SiteCode => {
  // Extract the country segment (after the language + separator), tolerant of
  // BOTH the FE/next-intl form `en-NZ` AND the raw Frontastic/CT locale the BFF
  // receives — underscore separator + optional codeset/currency modifier, e.g.
  // `en_NZ`, `en_NZ@NZD`. (An earlier exact `=== 'en-NZ'` check silently
  // defaulted the BFF's `en_NZ` to sdau, inverting the login guard.) Country
  // `NZ` → sdnz; every other locale (`en-AU`, the dev default, or a
  // country-less locale) → sdau.
  const country = /^[a-z]{2,}[_-]([A-Za-z0-9]{2,})/i.exec(locale ?? '')?.[1]?.toUpperCase();
  return country === 'NZ' ? 'sdnz' : 'sdau';
};

/**
 * Cross-region login check: `true` when the login must be rejected because the
 * dealer's region (BusinessUnit `siteCode`) does not match the site being
 * signed into — one customer, one region.
 *
 * Fail-CLOSED: a BU with no `siteCode` is ALSO rejected (the FE shows a generic
 * "contact customer support" message). `siteCode` is a required field on every
 * dealer BU, so an unset value is non-conformant data — not "any region". This
 * supersedes the earlier fail-OPEN rollout behaviour now that the BU backfill is
 * done (EWM-541). NOTE: this must not ship until the OneOffice provisioning
 * template also sets `siteCode` on (re)created BUs, or a re-sync re-locks those
 * dealers out. See the project_nz_region_gate memory.
 */
export const isCrossRegionLogin = (businessUnitSiteCode: string | undefined, siteCode: SiteCode): boolean =>
  !businessUnitSiteCode || businessUnitSiteCode !== siteCode;

// Domain attribute id for the CT variant `businessUnits` set attribute. Mirrors
// ProductMapper.generateAttributeIdFromAttributeName('businessUnits')
// (= `variants.attributes.${name}`); the mapped value is [{ key, label }, ...].
export const BUSINESS_UNITS_ATTRIBUTE_ID = 'variants.attributes.businessUnits';

// Every businessUnit/site key a product carries across its variants (deduped).
export const businessUnitKeysForProduct = (product: Product): string[] =>
  Array.from(
    new Set(
      (product?.variants ?? []).flatMap((variant) => {
        const value = variant?.attributes?.[BUSINESS_UNITS_ATTRIBUTE_ID];
        return Array.isArray(value) ? value.map((entry) => entry?.key).filter(Boolean) : [];
      }),
    ),
  );

// Whether a product is sold to a site (any variant's `businessUnits` ∋ siteCode).
// Fail-closed: a product carrying no such key is treated as NOT sold there.
export const isProductInSite = (product: Product, siteCode: SiteCode): boolean =>
  businessUnitKeysForProduct(product).includes(siteCode);

// ————————————— EWM-643: lifecycle `GO` (removed from sale), per site —————————————

// Domain attribute id for the CT variant `productLifeCycle` attribute (re-keyed
// `variants.attributes.${name}` like every mapped attribute). CT stores it as a
// JSON-ENCODED STRING of per-company entries — the company keys are the same
// siteCode vocabulary as everything else in this file:
//   [{ "company": "sdau", "values": { "lifeCycleAttribute": "GO", ... } },
//    { "company": "sdnz", "values": { "lifeCycleAttribute": "C",  ... } }]
export const PRODUCT_LIFECYCLE_ATTRIBUTE_ID = 'variants.attributes.productLifeCycle';

// The same attribute BEFORE ProductMapper re-keys it — `attributes[].name` on
// raw commercetools payloads (product-search projections, cart / shopping-list
// line-item variants). Cart and wishlist code reads raw CT shapes, so it keys
// on this; mapped-Product code keys on the `_ID` above.
export const PRODUCT_LIFECYCLE_ATTRIBUTE_NAME = 'productLifeCycle';

// `GO` = removed from sale: a temporary, REVERSIBLE per-country takedown (e.g.
// a quarantined product pulled for 24h without waiting on MDM syncs) — EWM-643.
export const REMOVED_FROM_SALE_LIFECYCLE_CODE = 'GO';

interface LifecycleEntry {
  company?: unknown;
  values?: { lifeCycleAttribute?: unknown };
}

/**
 * The lifecycle code for ONE site from the RAW attribute value (the
 * JSON-encoded string above). `undefined` when the value is missing,
 * unparseable, or carries no entry for the site. Shared by the backend gate
 * (via `lifecycleCodeForSite`) and the FE product mapper so the two parse the
 * SAME shape with the SAME rule and can never drift.
 */
export const lifecycleCodeFromRawAttribute = (raw: unknown, siteCode: SiteCode): string | undefined => {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  let entries: unknown;
  try {
    entries = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!Array.isArray(entries)) return undefined;
  const code = (entries as LifecycleEntry[]).find((entry) => entry?.company === siteCode)?.values?.lifeCycleAttribute;
  return typeof code === 'string' && code.length > 0 ? code : undefined;
};

// The product's lifecycle code for a site — from the FIRST variant carrying a
// parseable entry for that site's company.
//
// GRANULARITY SEAM, and it is INTENDED. This answers at PRODUCT level, so a
// multi-variant product whose first parseable variant is not GO passes
// `isProductRemovedFromSaleForSite` and therefore passes the BFF's
// `filterProductsForSite` — even if a LATER variant is GO. Consumers that work
// per VARIANT (the order uploader's `resolveSkus`, via
// `isRemovedFromSaleForLocale`) see that variant as removed from sale, so the
// two disagree by design rather than by accident.
//
// SETTLED — EWM-998. Luke Greenfield (PO), 2026-09-11: "Accept item not located
// as the error message. Note that all products have only one variant so both
// paths end up functioning the same." So the practical consequence is DECIDED,
// not an open question: an uploaded single-variant GO SKU is filtered out
// upstream and reads the uploader's DEFAULT unmatched-SKU message, and that is
// intended. That message is "SKU not found" (`quick-order.sku-not-found`, from
// `components/organisms/quick-order-upload`) — the "Item not located" Luke's
// wording quotes belongs to `quick-order-csv-upload`, a surface nothing mounts
// (EWM-989), so do not go looking for it in the drawer dealers actually use.
// Only the string in the record was wrong; the decision itself transfers.
// (Since EWM-949 that string is the FALLBACK the uploader shows absent a
// per-line reason; a single-variant GO SKU still lands on it, because it is
// filtered upstream and never reaches the row-level `removed-from-sale` reason
// EWM-949 adds — see `upload-line-availability.ts` for the full reconciliation.)
//
// Honest about the rule itself, and about which half of it is arbitrary.
// Variant 0 is NOT an arbitrary pick: in the BFF-mapped `Product` this reads,
// it is commercetools' MASTER variant —
// `ProductMapper.commercetoolsProductProjectionToVariants` pushes
// `productProjection.masterVariant` first and the projection's other variants
// after it — and the codebase already treats the master as the product's
// representative elsewhere (`primaryCategoryId` is read off it; the PDP's
// `useSupersededBy` names `variants[0].sku` `masterSku`). The effective rule is
// "the MASTER variant's code, falling back to the next variant carrying a
// parseable entry for the site".
//
// What IS arbitrary is that FALLBACK — nothing makes a non-master variant's
// lifecycle speak for the product when the master carries no parseable entry —
// and, beneath it, answering a per-VARIANT question at PRODUCT level at all.
// Both are safe only BECAUSE every product is single-variant, so the
// product-level and variant-level answers cannot disagree (measured 2026-09-17
// against `electus-dev`: no product carried more than one variant; for the live
// count read the `note` on `ct.product.single-variant` in the latest conformity
// run rather than a figure frozen here). In a multi-variant catalogue it would
// cut both ways, and the damaging direction is NOT the uploader's wording: a
// product that answers GO at product level — off its master, or off the
// fallback — is hidden wholesale by `filterProductsForSite`, taking its
// still-live variants off the storefront with it. That is the case to re-open
// on.
//
// The premise is MONITORED, not merely asserted — conformity invariant
// `ct.product.single-variant`
// (packages/electus/migration/src/conformity/invariants/ct.ts) re-counts
// multi-variant products on every conformity run. Know its reach: it is `warn`
// severity, so a hit prints a warning line with its offenders while the report
// still ends `RESULT: PASS` and leaves the run's exit code untouched — the CI
// conformity job and every release gate that reads `exit=$?` pass exactly as
// before. Only a human reading the per-invariant lines catches it — but four
// places do ask for that read: `/conformity-check`, which `/ship-pr` invokes for
// any PR whose dossier lists a data assumption, and the conformity gates in
// `.claude/commands/release-to-qa.md`, `release-to-staging.md` and
// `release-to-production.md` — the last of which puts it plainly: "Read the
// per-invariant lines, not only the exit code". `hotfix-to-prod.md` is the one
// gate that reads the exit code alone.
// Treat it as a tripwire several people are pointed at, not a gate that will
// stop anything on its own.
export const lifecycleCodeForSite = (product: Product, siteCode: SiteCode): string | undefined => {
  for (const variant of product?.variants ?? []) {
    const code = lifecycleCodeFromRawAttribute(variant?.attributes?.[PRODUCT_LIFECYCLE_ATTRIBUTE_ID], siteCode);
    if (code !== undefined) return code;
  }
  return undefined;
};

/**
 * EWM-643 — whether the product is REMOVED FROM SALE (`GO`) for a site.
 *
 * DELIBERATELY the opposite polarity to the NZ region gate below: a product is
 * hidden ONLY on an explicit `GO` match in this site's company entry. A
 * missing/garbled attribute — or no entry for the site — NEVER hides. GO is a
 * per-country temporary takedown; if a data gap counted as GO, one bad MDM
 * sync would wipe the whole catalogue.
 */
export const isProductRemovedFromSaleForSite = (product: Product, siteCode: SiteCode): boolean =>
  lifecycleCodeForSite(product, siteCode) === REMOVED_FROM_SALE_LIFECYCLE_CODE;

/**
 * EWM-972 — whether the product's `businessUnits` set permits this site.
 *
 * The two sites use deliberately different polarities:
 *
 * - **NZ (`sdnz`) is fail-CLOSED.** The key must be present. NZ availability is
 *   modelled ONLY by this attribute, so an absent one means "not sold in NZ".
 *
 * - **AU (`sdau`) is fail-open on a DATA GAP only.** A product carrying no
 *   `businessUnits` at all stays visible: AU is the historical catalogue
 *   superset and one bad MDM sync must never empty it. But a product that
 *   positively declares OTHER sites and not `sdau` is genuinely not sold on the
 *   Electus AU storefront, and is excluded.
 *
 * That distinction is the whole fix. `sdau` previously short-circuited to
 * `true`, so NZ-only products were never blocked on AU and the "both
 * directions" rule held only by accident — of the three NZ-only products, just
 * one was caught, and only because its AU lifecycle happened to be `GO`.
 *
 * Keeping the gap fail-open is what makes this safe to ship without first
 * proving `sdau` coverage in production: the only products whose visibility
 * changes are those that already name a different site.
 */
export const isProductSoldInSite = (product: Product, siteCode: SiteCode): boolean => {
  if (siteCode === 'sdnz') return isProductInSite(product, siteCode);
  const keys = businessUnitKeysForProduct(product);
  return keys.length === 0 || keys.includes(siteCode);
};

// Whether a product may be shown for a site — BOTH rules:
//   1. region: the site's `businessUnits` rule above (EWM-972 — now enforced in
//      BOTH directions, where `sdau` used to pass unconditionally), AND
//   2. lifecycle: not removed from sale (`GO`) for the site (EWM-643 —
//      explicit-match only; see isProductRemovedFromSaleForSite).
export const isProductAvailableForSite = (product: Product, siteCode: SiteCode): boolean =>
  isProductSoldInSite(product, siteCode) && !isProductRemovedFromSaleForSite(product, siteCode);

// Filter a product list for a site. (EWM-643 removed the old `sdau`
// pass-through shortcut — the GO takedown applies to BOTH sites, so AU lists
// are filtered too.)
export const filterProductsForSite = <T extends Product>(products: T[], siteCode: SiteCode): T[] =>
  products.filter((product) => isProductAvailableForSite(product, siteCode));

// The subset of `locales` a product may be advertised under (for hreflang),
// derived from the same rule as the gate.
export const localesAvailableForProduct = (product: Product, locales: readonly string[]): string[] =>
  locales.filter((locale) => isProductAvailableForSite(product, siteCodeFromLocale(locale)));
